<svelte:options customElement="clever-search-chat" />

<script lang="ts">
  import { state_busyApi, state_questionImageSearch } from './lib/store/state';
  import { onDestroy, onMount } from 'svelte';
  import { WidgetType } from './lib/enums/widget-type.enum';
  import { WindowsCustomEventTypes } from './lib/enums/windows-custom-event.enum';
  import { shadowDomUpdates } from './lib/helpers/shadowdom-updates';
  /** stores */
  import { ConfigStore } from './lib/store/config';
  import {
    state_answerRating,
    state_conversationResults,
    state_questionRelatedSearchSuggestions,
    state_searchResult,
    state_apiErrors,
    state_scrollingBehavior,
  } from './lib/store/state';
  import SearchInput from './lib/components/search-input.svelte';
  import SearchInputTheme from './lib/components/themes/search-input-theme.svelte';
  import ChatWrapper from './lib/components/chat-wrapper.svelte';
  import { fade } from 'svelte/transition';
  import { defaultLogger as logger } from './lib/telemetry/logger';

  import { _, t } from 'svelte-i18n';
  import { ChatbotAppearanceThemeType } from './lib/enums/themes/chatbot-appearance-theme-type.enum';
  import FileAttachmentButton from './lib/components/file-attachment-button.svelte';
  import FileAttachmentDropDrag from './lib/components/file-attachment-drop-drag.svelte';
  import { handleUploadFiles } from './lib/helpers/handle-upload-files';
  import { isFeatureEnabled, getEnabledFeatures } from './lib/helpers/features';
  import { WidgetFeatureType } from './lib/enums/widget-feature.enum';
  import { ApiTypes } from './lib/enums/api.enum';
  import SvgIcon from './lib/components/svg-icon.svelte';
  import MultiOptionInputTheme from './lib/components/themes/multi-option-input-theme .svelte';

  let contentWrapper: HTMLElement;
  let isDraggingOver = false;

  let scrollTimer: any;
  let isScroll: boolean = false;

  $: isSearchBusy = $state_busyApi.some((x) =>
    [
      ApiTypes.postQuestion,
      ApiTypes.streamPOSTQuestion, // added 07/24
      ApiTypes.postOption,
      ApiTypes.getOption,
      ApiTypes.getThread,
    ].includes(x)
  );

  // to make sure the config is correct, update view type
  // however, cb load chat, so avoid on cb
  if (
    $ConfigStore.widgetType !== WidgetType.cb &&
    $ConfigStore.widgetType !== WidgetType.cip &&
    $ConfigStore.widgetType !== WidgetType.ce &&
    $ConfigStore.widgetType !== WidgetType.ctt &&
    $ConfigStore.widgetType !== WidgetType.cmt
  )
    $ConfigStore = {
      ...$ConfigStore,
      widgetType: WidgetType.chat,
    };

  $: logoSrc = getLogoImg;

  // Mutation observer to detect new messages
  const observer = new MutationObserver(function () {
    checkIsScroll();
  });

  const getLogoImg = (): string => {
    if ($ConfigStore.agentImg) return $ConfigStore.agentImg;

    if ($ConfigStore.logoMini?.length) {
      if ($ConfigStore.logoMini?.substring(0, 4) == 'http')
        return $ConfigStore.logoMini;

      return (
        ($ConfigStore.assetBaseUrl || $ConfigStore.apiBaseUrl) +
        $ConfigStore.logoMini
      );
    }

    if ($ConfigStore.logoMain?.length) {
      if ($ConfigStore.logoMain?.substring(0, 4) == 'http')
        return $ConfigStore.logoMain;

      return (
        ($ConfigStore.assetBaseUrl || $ConfigStore.apiBaseUrl) +
        $ConfigStore.logoMain
      );
    }

    return '';
  };
  onMount(() => {
    shadowDomUpdates('clever-search-chat', true);

    // Add scroll event listener
    if ($ConfigStore.scrollBehavior) {
      // Add scroll event listener at first
      if (contentWrapper) {
        scrollEvent(100);
      }
      // Add scroll event listener
      if ($ConfigStore.scrollBehavior) {
        contentWrapper.addEventListener('scroll', () => {
          checkIsScroll();
        });
      }
      // Add mutation observer to detect new messages
      observer.observe(contentWrapper, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false,
      });
    }
  });

  const restartConversation = () => {
    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.restartThread, {
        detail: {
          configOptions: $ConfigStore,
          serverConfig: $ConfigStore.serverConfig,
        },
      })
    );

    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.showCsatPopup, {
        detail: {
          isRated: false,
          isNotification: false,
          lengthOfQuestions: 0,
        },
      })
    );
    state_questionImageSearch.set([]);
    state_conversationResults.set([]);
    state_searchResult.set(null);
    state_questionRelatedSearchSuggestions.set(null);
    state_answerRating.set(null);
    state_apiErrors.set([]);

    logger.trackEvent('Restart Conversation', {
      tenant: $ConfigStore.serverConfig?.tenant_id,
    });
  };

  // Change isScroll to show or hide scroll button
  const checkIsScroll = () => {
    if (
      contentWrapper &&
      contentWrapper.scrollTop <
        contentWrapper.scrollHeight - contentWrapper.clientHeight - 100
    ) {
      isScroll = true;
    } else {
      isScroll = false;
    }
  };

  // Scroll to bottom event with timeout
  const scrollEvent = (timeout: number = 10) => {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      //console.log("SCROLLING TO BOTTOM", contentWrapper.scrollHeight);
      contentWrapper.scrollTop = contentWrapper.scrollHeight - 100;
      clearTimeout(scrollTimer);
    }, timeout);
  };

  // conversation results state subscription to scroll new items to screen (original scroll behavior)
  state_conversationResults.subscribe((x) => {
    if (contentWrapper && !$ConfigStore.scrollBehavior) {
      scrollEvent();
    }
  });

  //  subscribe to scrolling behavior state to manage scroll () new scroll behavior)
  state_scrollingBehavior.subscribe((x) => {
    if (contentWrapper && $ConfigStore.scrollBehavior) {
      scrollEvent(100);
    }
  });
  state_questionRelatedSearchSuggestions.subscribe((x) => {
    if (contentWrapper && $ConfigStore.scrollBehavior && x?.data) {
      state_scrollingBehavior.set(!$state_scrollingBehavior);
    } else if (!$ConfigStore.scrollBehavior) {
      scrollEvent(100);
    }
  });
  const scrollToBottom = (): void => {
    if (!contentWrapper) return;

    if (
      !isSearchBusy &&
      contentWrapper.scrollTop !=
        contentWrapper.scrollHeight - contentWrapper.clientHeight &&
      contentWrapper.scrollHeight > 0
    ) {
      state_scrollingBehavior.set(!$state_scrollingBehavior);
      window.dispatchEvent(
        new CustomEvent(WindowsCustomEventTypes.focusSearchInput)
      );
    }
    return;
  };

  const handleUpload = (e: DragEvent) => {
    isDraggingOver = false;

    if (
      e.dataTransfer &&
      isFeatureEnabled($ConfigStore, WidgetFeatureType.addFiles)
    ) {
      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;

      handleUploadFiles(files);
    }
  };

  onDestroy(() => {
    observer.disconnect();
    contentWrapper?.removeEventListener('scroll', () => {
      checkIsScroll();
    });
  });
</script>

<div
  class={`cs-chat-wrapper cs-type-${$ConfigStore.widgetType}
  ${$ConfigStore?.isIdentifyName ? 'cs-identify-name' : ''}
  ${$ConfigStore.showBranding ? 'cs-branding-on' : ''}
  ${$ConfigStore.customTheme.chatbotAppearanceThemeType === ChatbotAppearanceThemeType.curve ? 'cs-wrapper-curve' : ''}
   ${$ConfigStore.customTheme?.smallSizeThemeType === true ? 'cs-wrapper-small' : ''}`}
  style:background={$ConfigStore.customTheme.bodyBg}
  style:border-color={$ConfigStore.customTheme.bodyBorderColor}
  in:fade|global
>
  <div
    class={`cs-content-wrapper ${$ConfigStore.scrollBehavior ? 'cs-behavior-scroll' : ''}`}
    bind:this={contentWrapper}
    tabindex="0"
  >
    <ChatWrapper />
  </div>

  {#if isScroll && $ConfigStore.scrollBehavior}
    <button
      aria-label={$_('scroll_down')}
      class="cs-scroll-down"
      on:click={scrollToBottom}
    >
      <!-- Uploaded to: SVG Repo, www.svgrepo.com, Generator: SVG Repo Mixer Tools -->
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        ><path
          d="M26.29 20.29 18 28.59V0h-2v28.59l-8.29-8.3-1.42 1.42 10 10a1 1 0 0 0 1.41 0l10-10z"
          data-name="2-Arrow Down"
        /></svg
      >
    </button>
  {/if}

  <div
    class="cs-input-wrapper"
    role="region"
    on:dragenter={() =>
      (isDraggingOver =
        true && isFeatureEnabled($ConfigStore, WidgetFeatureType.addFiles))}
    on:dragleave|preventDefault={() =>
      (isDraggingOver =
        false && isFeatureEnabled($ConfigStore, WidgetFeatureType.addFiles))}
    on:dragover|preventDefault={() =>
      (isDraggingOver =
        true && isFeatureEnabled($ConfigStore, WidgetFeatureType.addFiles))}
    on:drop|preventDefault={handleUpload}
  >
    <div>
      {#if ($ConfigStore.canClearChatHistory && getEnabledFeatures($ConfigStore).length > 0) || getEnabledFeatures($ConfigStore).length > 1}
        <div
          class="cs-multi-option-wrapper"
          class:cs-has-mode-switch={$ConfigStore.modeSwitchOptions?.length}
        >
          <MultiOptionInputTheme>
            <svelte:fragment slot="dropdownElement" let:close>
              {#if $state_conversationResults.length}
                <li role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected="false"
                    class="cs-mode-switch__item"
                    on:click={() => {
                      restartConversation();
                      close();
                    }}
                  >
                    <SvgIcon name="brush-cleaning" />
                    {$_('search_input.restart_conversation')}
                  </button>
                </li>
              {/if}
              {#each getEnabledFeatures($ConfigStore) as feature (feature.name)}
                {#if feature.name === WidgetFeatureType.addFiles}
                  <li role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected="false"
                      class="cs-mode-switch__item"
                    >
                      <FileAttachmentButton on:close={close} />
                    </button>
                  </li>
                {:else}
                  <li role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected="false"
                      class="cs-mode-switch__item"
                      on:click={close}
                    >
                      <SvgIcon name={feature.icon ?? 'puzzle'} />
                      {feature.label ?? feature.name}
                    </button>
                  </li>
                {/if}
              {/each}
            </svelte:fragment>
          </MultiOptionInputTheme>
        </div>
      {:else}
        {#if $ConfigStore.canClearChatHistory}
          <div class="logo">
            {#if $state_conversationResults.length}
              <button
                in:fade|global
                on:click={() => restartConversation()}
                aria-label={$_('search_input.restart_conversation')}
                title={$_('search_input.restart_conversation')}
                ><SvgIcon name="brush-cleaning" /></button
              >
            {:else}
              <!--><img
                            in:fade|global
                            src={logoSrc()}
                            alt={$ConfigStore.productName}
                        />-->
              <button
                in:fade|global
                style="opacity: 0.2"
                disabled
                aria-label={$_('search_input.restart_conversation_disabled')}
                ><SvgIcon name="brush-cleaning" /></button
              >
            {/if}
          </div>
        {/if}
        {#if isFeatureEnabled($ConfigStore, WidgetFeatureType.addFiles)}
          <div class="cs-btn-file-attachment">
            <FileAttachmentButton />
          </div>
          <div
            class="cs-file-attachment-drop-drag"
            class:active={isDraggingOver}
          >
            <FileAttachmentDropDrag />
          </div>
        {/if}
      {/if}
      {#if $ConfigStore.isTextAreaSearch}
        <SearchInputTheme />
      {:else}
        <SearchInput />
      {/if}
    </div>
    {#if $ConfigStore.showBranding}
      <div class="cs-branding-wrapper">
        Powered by
        <a href="https://www.conversagent.app/" target="_blank"
          ><img
            alt="Conversagent"
            src="https://components.clevertar.app/gqa/conversagent-logo.png"
          /> Conversagent</a
        >
      </div>
    {/if}
  </div>
</div>
{#if $ConfigStore.customCSSUrl}
  {#if $ConfigStore.customCSSIntegrity}
    <link
      href={$ConfigStore.customCSSUrl}
      rel="stylesheet"
      type="text/css"
      integrity={$ConfigStore.customCSSIntegrity}
      crossorigin="anonymous"
    />
  {:else}
    <link href={$ConfigStore.customCSSUrl} rel="stylesheet" type="text/css" />
  {/if}
{/if}

<style lang="scss">
  @use './scss/pages/chat';
</style>
